import React, { useEffect, useState } from 'react';
import { api } from '@/api/apiClient';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function DeleteAccount() {
  const { impersonating } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (impersonating) return;
    api.request('GET', '/auth/deletion-request').then(setRequest)
      .catch(err => setError(err.message)).finally(() => setLoading(false));
  }, [impersonating]);
  async function submit() {
    setBusy(true); setError('');
    try {
      const result = await api.request('DELETE', '/auth/me', { confirmation });
      setRequest(result); setOpen(false); setConfirmation('');
    } catch (err) { setError(err.message || 'Request failed. Please try again.'); }
    finally { setBusy(false); }
  }
  if (impersonating) return null;
  const pending = request && ['pending', 'processing', 'failed', 'deleted'].includes(request.status);
  return <section className="my-6 p-4 border border-red-200 rounded-xl space-y-3">
    <h2 className="font-semibold">Delete account</h2>
    <p className="text-sm text-slate-600">Step 1: Request deletion. Step 2: A manager confirms that you no longer work with us and approves permanent deletion of your account, personal documents and associated portal records. Nothing is deleted when you submit your request.</p>
    {error && <p role="alert" className="text-red-700 text-sm">{error}</p>}
    {pending ? <p role="status" className="text-sm font-medium">
      {request.status === 'pending' ? 'Deletion requested — awaiting manager approval. Your account will not be deleted until approval.'
        : 'Your deletion has been approved. A manager is completing the account removal.'}
    </p> : <>
      {request?.status === 'rejected' && <p role="status" className="text-sm">A manager declined your previous request. Contact your manager if you need to discuss it, or submit a new request.</p>}
      {!open ? <Button variant="destructive" disabled={loading} onClick={() => setOpen(true)}>Request account deletion</Button> : <>
        <label htmlFor="delete-confirmation" className="block text-sm">Type DELETE to submit your request</label>
        <Input id="delete-confirmation" value={confirmation} onChange={e => setConfirmation(e.target.value)} autoComplete="off" disabled={busy} />
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" disabled={busy} onClick={() => { setOpen(false); setConfirmation(''); }}>Cancel</Button>
          <Button variant="destructive" disabled={busy || confirmation !== 'DELETE'} onClick={submit}>{busy ? 'Submitting...' : 'Submit deletion request'}</Button>
        </div>
      </>}
    </>}
  </section>;
}
